// src/components/personnes/ChoixPersonne.tsx — SCE Issues
// Choisir une personne dans l'annuaire de l'entreprise (leader, expert,
// responsable d'action FNC-T). Maquette validée le 07/10 :
// https://claude.ai/artifact/YCVA97H2516a1DewiMP4bE
//
//  1. Champ vide : les habitués (noms les plus fréquents du contexte).
//  2. Deux lettres : l'annuaire de Pompey seulement (profil `Office`) — depuis
//     le 22/10, plus de « Chercher hors Pompey » : les autres sites
//     n'intéressent pas l'usine —, puis les externes si le parent les demande
//     (`invites`) : ceux du panneau d'administration SEULEMENT. Les invités du
//     tenant ne sont jamais proposés : ce sont des comptes personnels
//     (téléphone, ordinateur, iPad), pas des externes (22/10).
//     ↑ ↓ pour choisir, Entrée pour valider, Échap pour fermer.
//  3. Choisie : une étiquette (photo, nom, service — poste) et une croix.
//
// JAMAIS de personne hors annuaire (Guillaume, 07/10). Un nom déjà là sans
// adresse (pré-rempli, habitué, repris de l'Excel) est retrouvé en silence
// dans l'annuaire s'il n'y en a qu'un ; sinon il reste tel quel, pastille
// pointillée (historique). Au téléphone, la recherche s'ouvre en feuille.

import { useEffect, useRef, useState, type JSX, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { Search, X } from 'lucide-react'
import { chercherParNom, personneDe, rechercherPompey, retrouverPersonne, type PersonneRetrouvee } from '@/api/annuaire.api'
import { partagerParSite, type Personne } from '@/utils/annuaire'
import { rolesCharges } from '@/config/registreRoles'
import { nomExterne, rechercherExternes } from '@/utils/roles'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { PastillePersonne } from './PastillePersonne'

export interface PersonneChoisie {
  nom: string
  upn: string
  service: string
  /**
   * Vrai : personne RETROUVÉE seule d'après un nom déjà là (pas un choix de
   * l'utilisateur) — n'en reprendre que l'adresse, jamais le nom ni le service.
   */
  retrouvee?: boolean
}

export interface ChoixPersonneProps {
  /** id du champ, pour le <label htmlFor> du parent. */
  id?: string
  /** Ce qu'on choisit (« Responsable », « Leader ») : lecteurs d'écran, en-tête de la feuille. */
  libelle: string
  valeur: { nom: string; upn?: string }
  /** null : la personne est retirée (la croix). */
  onChoisir: (p: PersonneChoisie | null) => void
  /**
   * Proposés quand le champ est vide, du plus au moins fréquent : un nom (retrouvé
   * dans l'annuaire au clic) ou une personne déjà connue par son adresse.
   */
  habitues?: readonly (string | { nom: string; upn: string })[]
  titreHabitues?: string
  /** Ligne d'action : plus serré, le service passe en infobulle. */
  compact?: boolean
  /** Le curseur dans la recherche dès l'affichage (champ ouvert par un bouton « + Quelqu'un d'autre »). */
  autoFocus?: boolean
  /** Montrer aussi les externes du panneau d'administration : section « Externes », leur adresse mail comme identifiant. */
  invites?: boolean
}

export function ChoixPersonne(props: ChoixPersonneProps): JSX.Element {
  const [focus, setFocus] = useState(props.autoFocus ?? false)
  if (props.valeur.nom.trim()) {
    return (
      <Etiquette
        {...props}
        onVider={() => {
          setFocus(true)
          props.onChoisir(null)
        }}
      />
    )
  }
  return <Recherche {...props} autoFocus={focus} />
}

// ─── L'étiquette d'une personne choisie ───

/** Ce que dit l'étiquette d'un nom sans adresse qu'on n'a pas pu retrouver. */
const MANQUES: Record<PersonneRetrouvee['raison'], string> = {
  trouve: '',
  absent: 'Pas retrouvé dans l’annuaire',
  plusieurs: 'Plusieurs dans l’annuaire : × pour choisir le bon',
  erreur: 'Annuaire injoignable : réessayé à la prochaine ouverture',
}

function Etiquette({ libelle, valeur, onChoisir, compact, onVider }: ChoixPersonneProps & { onVider: () => void }): JSX.Element {
  const [info, setInfo] = useState<Personne | null>(null)
  // Pourquoi un nom sans adresse n'est pas retrouvé (null : rien à dire).
  const [manque, setManque] = useState<PersonneRetrouvee['raison'] | null>(null)
  const introuvable = manque !== null
  const choisir = useRef(onChoisir)
  choisir.current = onChoisir

  useEffect(() => {
    let vivante = true
    setInfo(null)
    setManque(null)
    if (valeur.upn) {
      void personneDe(valeur.upn, valeur.nom).then((p) => vivante && setInfo(p))
    } else {
      // Un nom sans adresse : retrouvé s'il n'y en a qu'un, sinon gardé (historique).
      void chercherParNom(valeur.nom).then(({ personne: p, raison }) => {
        if (!vivante) return
        if (!p) {
          setManque(raison)
          return
        }
        setInfo(p) // photo et service tout de suite, même si le parent ne garde pas l'adresse (expert)
        choisir.current({ nom: p.nom, upn: p.upn, service: p.service, retrouvee: true })
      })
    }
    return () => {
      vivante = false
    }
  }, [valeur.upn, valeur.nom])

  const detail = info ? [info.service, info.poste].filter(Boolean).join(' — ') : manque ? MANQUES[manque] : ''
  return (
    <div
      title={detail ? `${valeur.nom} · ${detail}` : valeur.nom}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 44,
        padding: compact ? '0 0 0 8px' : '4px 0 4px 8px',
        boxSizing: 'border-box',
        border: '1px solid var(--color-border)',
        backgroundColor: 'var(--color-white)',
        minWidth: 0,
      }}
    >
      <PastillePersonne nom={valeur.nom} upn={valeur.upn ?? info?.upn} taille={compact ? 28 : 32} pointillee={introuvable} />
      <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <span style={{ ...UNE_LIGNE, fontSize: 15, fontWeight: compact ? 400 : 600 }}>{valeur.nom}</span>
        {!compact && detail && <span style={{ ...UNE_LIGNE, fontSize: 12, color: 'var(--color-text-secondary)' }}>{detail}</span>}
      </span>
      <button type="button" aria-label={`${libelle} : changer de personne`} onClick={onVider} style={BOUTON_ICONE}>
        <X size={16} />
      </button>
    </div>
  )
}

// ─── La recherche ───

interface Option {
  cle: string
  nom: string
  upn?: string
  detail: string
  /** Absente pour un habitué : on le retrouve dans l'annuaire au clic. */
  personne?: Personne
}

interface EtatRecherche {
  texte: string
  setTexte: (t: string) => void
  options: Option[]
  /** Ce que la liste affiche, section par section. */
  sections: { titre: string; options: Option[] }[]
  message: string | null
  aide: string | null
  choisir: (o: Option) => void
}

/** Les externes du panneau d'administration qui répondent à la recherche, en options (adresse mail comme identifiant). */
function externesListe(texte: string): Option[] {
  return rechercherExternes(rolesCharges()?.externes ?? [], texte).map((e) => ({
    cle: `x:${e.email}`,
    nom: nomExterne(e),
    upn: e.email,
    detail: ['Externe', e.societe].filter(Boolean).join(' · '),
  }))
}

function detailDe(p: Personne): string {
  return [p.service, p.poste].filter(Boolean).join(' — ')
}

function useRecherche({ habitues = [], titreHabitues = 'Souvent choisis', onChoisir, invites = false }: ChoixPersonneProps, apresChoix: () => void): EtatRecherche {
  const [texte, setTexte] = useState('')
  const [resultats, setResultats] = useState<Personne[] | null>(null)
  const [chargement, setChargement] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [retrouve, setRetrouve] = useState<string | null>(null)
  const derniere = useRef(0)

  useEffect(() => {
    const q = texte.trim()
    if (q.length < 2) {
      setResultats(null)
      setErreur(null)
      setChargement(false)
      return
    }
    setChargement(true)
    const n = ++derniere.current
    const minuterie = window.setTimeout(() => {
      // Jamais les invités du tenant : ce sont des comptes PERSONNELS (téléphone,
      // ordinateur, iPad — comptes secondaires), pas des externes (22/10). Les
      // externes viennent de la liste du panneau d'administration.
      rechercherPompey(q, 20)
        .then((r) => {
          if (n !== derniere.current) return
          setResultats(r)
          setErreur(null)
        })
        .catch((err: unknown) => {
          if (n !== derniere.current) return
          setResultats(null)
          setErreur(err instanceof Error ? err.message : String(err))
        })
        .finally(() => n === derniere.current && setChargement(false))
    }, 250)
    return () => window.clearTimeout(minuterie)
  }, [texte])

  const choisir = (o: Option): void => {
    if (o.personne) {
      onChoisir({ nom: o.personne.nom, upn: o.personne.upn, service: o.personne.service })
      apresChoix()
      return
    }
    if (o.upn) {
      // Un habitué déjà connu par son adresse : rien à chercher.
      onChoisir({ nom: o.nom, upn: o.upn, service: '' })
      apresChoix()
      return
    }
    // Un habitué : retrouvé dans l'annuaire ; sinon, on le cherche sous les yeux de l'utilisateur.
    setRetrouve(o.nom)
    void retrouverPersonne(o.nom).then((p) => {
      setRetrouve(null)
      if (p) {
        onChoisir({ nom: p.nom, upn: p.upn, service: p.service })
        apresChoix()
      } else setTexte(o.nom)
    })
  }

  const enRecherche = texte.trim().length >= 2
  const sections: EtatRecherche['sections'] = []
  let message: string | null = null
  let aide: string | null = null

  if (!enRecherche) {
    const h = habitues.map<Option>((x) =>
      typeof x === 'string' ? { cle: `h:${x}`, nom: x, detail: '' } : { cle: `h:${x.upn}`, nom: x.nom, upn: x.upn, detail: '' },
    )
    if (h.length) sections.push({ titre: titreHabitues, options: h })
    aide = 'Tapez deux lettres pour chercher dans tout l’annuaire'
    if (retrouve) message = `Recherche de ${retrouve} dans l’annuaire…`
  } else if (erreur && !(invites && externesListe(texte).length)) {
    message = `L’annuaire ne répond pas : ${erreur}`
  } else if (chargement && !resultats) {
    message = 'Recherche…'
  } else if (resultats) {
    const { pompey, autres } = partagerParSite(resultats)
    const enOptions = (ps: Personne[]): Option[] => ps.map((p) => ({ cle: p.upn, nom: p.nom, upn: p.upn, detail: detailDe(p), personne: p }))
    // Pompey seulement : les autres sites n'intéressent pas l'usine (Guillaume, 22/10).
    if (pompey.length) sections.push({ titre: 'Pompey', options: enOptions(pompey) })
    const externes = invites ? externesListe(texte) : []
    if (externes.length) sections.push({ titre: 'Externes', options: externes })
    if (!sections.length) message = autres.length ? 'Personne de ce nom à Pompey' : 'Personne de ce nom dans l’annuaire'
  }

  return {
    texte,
    setTexte,
    options: sections.flatMap((s) => s.options),
    sections,
    message,
    aide,
    choisir,
  }
}

function Recherche(props: ChoixPersonneProps & { autoFocus: boolean }): JSX.Element {
  const estBureau = useEstBureau()
  const [ouvert, setOuvert] = useState(false)
  const [actif, setActif] = useState(0)
  const champ = useRef<HTMLInputElement>(null)
  const idListe = `${props.id ?? 'personne'}-liste`
  const r = useRecherche(props, () => setOuvert(false))

  useEffect(() => {
    if (props.autoFocus) {
      if (estBureau) champ.current?.focus()
      else setOuvert(true)
    }
  }, [props.autoFocus, estBureau])

  useEffect(() => setActif(0), [r.texte, r.options.length])

  const surTouche = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOuvert(true)
      setActif((i) => Math.min(i + 1, r.options.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActif((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      const o = r.options[actif]
      if (ouvert && o) {
        e.preventDefault()
        r.choisir(o)
      }
    } else if (e.key === 'Escape' && ouvert) {
      // Ferme la liste, pas la fenêtre dessous.
      e.preventDefault()
      e.stopPropagation()
      setOuvert(false)
    }
  }

  const liste = <Liste r={r} actif={actif} idListe={idListe} id={props.id} feuille={!estBureau} />

  if (!estBureau) {
    return (
      <>
        <button
          id={props.id}
          type="button"
          aria-label={`${props.libelle} : chercher un nom`}
          onClick={() => setOuvert(true)}
          style={{ ...STYLE_CHAMP_RECHERCHE, width: '100%', cursor: 'pointer', color: 'var(--color-text-secondary)', textAlign: 'left' }}
        >
          <Search size={16} aria-hidden="true" />
          <span style={{ fontSize: 15 }}>Chercher un nom</span>
        </button>
        {ouvert &&
          createPortal(
            <div role="presentation" style={STYLE_VOILE} onClick={(e) => e.target === e.currentTarget && setOuvert(false)}>
              <div role="dialog" aria-modal="true" aria-label={props.libelle} style={STYLE_FEUILLE}>
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
                  <span style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: 'var(--color-border)' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px 4px 16px' }}>
                  <span style={{ fontSize: 17, fontWeight: 700 }}>{props.libelle}</span>
                  <button type="button" aria-label="Fermer" onClick={() => setOuvert(false)} style={BOUTON_ICONE}>
                    <X size={20} />
                  </button>
                </div>
                <div style={{ padding: '0 16px 8px' }}>
                  <div style={{ ...STYLE_CHAMP_RECHERCHE, border: '2px solid var(--color-black)', borderRadius: 8, minHeight: 48 }}>
                    <Search size={18} aria-hidden="true" color="var(--color-text-secondary)" />
                    <input
                      ref={champ}
                      autoFocus
                      role="combobox"
                      aria-expanded="true"
                      aria-controls={idListe}
                      aria-label={`${props.libelle} : chercher un nom`}
                      value={r.texte}
                      onChange={(e) => r.setTexte(e.target.value)}
                      onKeyDown={surTouche}
                      placeholder="Chercher un nom"
                      style={{ ...STYLE_INPUT, fontSize: 17 }}
                    />
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>{liste}</div>
              </div>
            </div>,
            document.body,
          )}
      </>
    )
  }

  return (
    <div style={{ position: 'relative', minWidth: 0 }}>
      <div style={{ ...STYLE_CHAMP_RECHERCHE, border: `${ouvert ? 2 : 1}px solid ${ouvert ? 'var(--color-black)' : 'var(--color-border)'}` }}>
        <Search size={16} aria-hidden="true" color="var(--color-text-secondary)" />
        <input
          ref={champ}
          id={props.id}
          role="combobox"
          aria-expanded={ouvert}
          aria-controls={idListe}
          aria-autocomplete="list"
          aria-activedescendant={ouvert && r.options[actif] ? `${idListe}-${actif}` : undefined}
          aria-label={`${props.libelle} : chercher un nom`}
          value={r.texte}
          onChange={(e) => {
            r.setTexte(e.target.value)
            setOuvert(true)
          }}
          onFocus={() => setOuvert(true)}
          onBlur={() => setOuvert(false)}
          onKeyDown={surTouche}
          placeholder="Chercher un nom"
          autoComplete="off"
          style={STYLE_INPUT}
        />
      </div>
      {ouvert && (
        // mousedown : le clic dans la liste ne doit pas faire perdre le focus au champ (blur = fermeture).
        <div onMouseDown={(e) => e.preventDefault()} style={STYLE_MENU}>
          {liste}
        </div>
      )}
    </div>
  )
}

function Liste({ r, actif, idListe, feuille }: { r: EtatRecherche; actif: number; idListe: string; id?: string; feuille: boolean }): JSX.Element {
  let rang = -1
  const tailleLigne = feuille ? 60 : 44
  return (
    <div>
      <ul id={idListe} role="listbox" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {r.sections.map((s) => (
          <li key={s.titre} role="presentation">
            <div style={{ padding: feuille ? '12px 16px 4px' : '8px 12px 4px', fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.titre}</div>
            <ul role="presentation" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {s.options.map((o) => {
                rang += 1
                const i = rang
                const estActif = i === actif && !feuille
                return (
                  <li
                    key={o.cle}
                    id={`${idListe}-${i}`}
                    role="option"
                    aria-selected={estActif}
                    onClick={() => r.choisir(o)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      minHeight: tailleLigne,
                      padding: feuille ? '8px 16px' : '6px 12px',
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      backgroundColor: estActif ? 'var(--color-yellow)' : 'transparent',
                    }}
                  >
                    <PastillePersonne nom={o.nom} upn={o.upn} taille={feuille ? 40 : 32} />
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span style={{ ...UNE_LIGNE, fontSize: feuille ? 17 : 15, fontWeight: 600 }}>{o.nom}</span>
                      {o.detail && (
                        <span style={{ ...UNE_LIGNE, fontSize: feuille ? 13 : 12, color: estActif ? 'var(--color-black)' : 'var(--color-text-secondary)' }}>
                          {o.detail}
                        </span>
                      )}
                    </span>
                  </li>
                )
              })}
            </ul>
          </li>
        ))}
      </ul>
      {r.message && <p style={{ margin: 0, padding: feuille ? '12px 16px' : '10px 12px', fontSize: 13, color: 'var(--color-text-secondary)' }}>{r.message}</p>}
      {r.aide && !feuille && (
        <div style={{ padding: '8px 12px 10px', fontSize: 12, color: 'var(--color-text-secondary)', borderTop: r.sections.length ? '1px solid var(--color-border)' : 'none' }}>
          {r.aide}
        </div>
      )}
    </div>
  )
}

// ─── Styles ───

const UNE_LIGNE = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const

const BOUTON_ICONE = {
  width: 44,
  height: 44,
  flexShrink: 0,
  border: 'none',
  background: 'transparent',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
} as const

const STYLE_CHAMP_RECHERCHE = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 44,
  padding: '0 10px',
  boxSizing: 'border-box',
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-white)',
} as const

const STYLE_INPUT = {
  border: 'none',
  outline: 'none',
  font: 'inherit',
  fontSize: 15,
  flex: 1,
  minWidth: 0,
  background: 'transparent',
} as const

const STYLE_MENU = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  minWidth: 300,
  zIndex: 30,
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-border)',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
  maxHeight: 360,
  overflowY: 'auto',
} as const

const STYLE_VOILE = {
  position: 'fixed',
  inset: 0,
  zIndex: 90,
  backgroundColor: 'rgba(0, 0, 0, 0.45)',
} as const

const STYLE_FEUILLE = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  top: 64,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--color-white)',
  borderRadius: '16px 16px 0 0',
} as const
